import type { FeatureResponse } from '@monorepo/shared/schemas/features'
import { DollarSign } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'

interface FeatureCostSignalsTableProps {
  costSignals: NonNullable<FeatureResponse['cost_signals']>
}

export function FeatureCostSignalsTable({
  costSignals,
}: FeatureCostSignalsTableProps) {
  if (!costSignals || costSignals.length === 0) {
    return null
  }

  return (
    <div className='border border-gray-200/60 dark:border-gray-800/60 rounded-lg overflow-hidden'>
      <table className='w-full'>
        <thead className='bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200/60 dark:border-slate-800/60'>
          <tr>
            <th className='px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300'>
              Signal Name
            </th>
            <th className='px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300'>
              Signal Code
            </th>
            <th className='px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300'>
              Unit Label
            </th>
            <th className='px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300'>
              Fixed Cost
            </th>
            <th className='px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300'>
              Category
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-slate-200/60 dark:divide-slate-800/60'>
          {costSignals.map((costSignal, index) => (
            <tr
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              key={index}
              className='hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors'
            >
              <td className='px-4 py-3'>
                <div className='font-semibold text-slate-900 dark:text-slate-100'>
                  {costSignal.signal_name}
                </div>
                {costSignal.description && (
                  <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>
                    {costSignal.description}
                  </div>
                )}
              </td>
              <td className='px-4 py-3'>
                <code className='text-sm font-mono text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded'>
                  {costSignal.signal_code}
                </code>
              </td>
              <td className='px-4 py-3 text-slate-700 dark:text-slate-300'>
                {costSignal.unit_label}
              </td>
              <td className='px-4 py-3'>
                {costSignal.cost_config?.fixed_cost_cents != null ? (
                  <div className='flex items-center gap-2 text-orange-600 dark:text-orange-400 font-semibold'>
                    <DollarSign className='w-4 h-4' />$
                    {(costSignal.cost_config.fixed_cost_cents / 100).toFixed(2)}
                  </div>
                ) : (
                  <span className='text-slate-400'>-</span>
                )}
              </td>
              <td className='px-4 py-3'>
                {costSignal.cost_config?.cost_category ? (
                  <Badge variant='outline' className='capitalize'>
                    {costSignal.cost_config.cost_category}
                  </Badge>
                ) : (
                  <span className='text-slate-400'>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
